import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Upload, Trash2, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nContext";
import { translateApiError } from "@/utils/apiErrors";
import { useToast } from "@/hooks/use-toast";
import api, { API } from "@/lib/api";
import { VOICE_RECORDER } from "@/constants/testIds";

/**
 * Reusable voice profile recorder: lets the user record a short sample with their microphone
 * (MediaRecorder API) or upload an audio file, preview it, then save it once (POST /voice/sample).
 * Used both in the Profile "Profil vocal" section and inline in the wizard's voice message step.
 */
export default function VoiceRecorder({ onChange }) {
  const { t, language } = useI18n();
  const { toast } = useToast();
  const [sample, setSample] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState(false);
  const [pendingBlob, setPendingBlob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const fetchSample = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/voice/sample");
      setSample(data.has_sample ? data : null);
    } catch (err) {
      // best-effort: leave sample as null
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSample();
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (e) => chunksRef.current.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setPendingBlob(blob);
        stream.getTracks().forEach((tr) => tr.stop());
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
    } catch (err) {
      toast({ variant: "destructive", title: t("common.error"), description: t("dashboard.voiceMicError") });
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setPendingBlob(file);
  };

  const uploadPending = async () => {
    if (!pendingBlob) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", pendingBlob, pendingBlob.name || "recording.webm");
      const { data } = await api.post("/voice/sample", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setSample({ has_sample: true, ...data });
      setPendingBlob(null);
      toast({ title: t("dashboard.voiceSaved") });
      onChange?.(true);
    } catch (err) {
      toast({ variant: "destructive", title: t("common.error"), description: translateApiError(err?.response?.data?.detail, language) });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete("/voice/sample");
      setSample(null);
      onChange?.(false);
      toast({ title: t("common.success") });
    } catch (err) {
      toast({ variant: "destructive", title: t("common.error"), description: translateApiError(err?.response?.data?.detail, language) });
    }
  };

  const token = localStorage.getItem("ams_token");
  const previewUrl = pendingBlob
    ? URL.createObjectURL(pendingBlob)
    : sample?.storage_path
    ? `${API}/files/${sample.storage_path}?auth=${token}`
    : null;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("common.loading")}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={VOICE_RECORDER.container}>
      {!pendingBlob && sample && (
        <div className="flex flex-col gap-3 rounded-xl border border-border bg-card/50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            <span data-testid={VOICE_RECORDER.status}>
              {t("dashboard.voiceSampleSavedOn")} {new Date(sample.updated_at).toLocaleDateString()} ({Math.round(sample.duration_seconds || 0)}s)
            </span>
          </div>
          <audio controls src={previewUrl} className="h-9 w-full sm:w-56" data-testid={VOICE_RECORDER.player} />
        </div>
      )}

      {!pendingBlob && (
        <div className="flex flex-wrap gap-3">
          {!recording ? (
            <Button type="button" variant="outline" className="rounded-full" data-testid={VOICE_RECORDER.recordBtn} onClick={startRecording}>
              <Mic className="mr-2 h-4 w-4" />
              {sample ? t("dashboard.voiceReRecordBtn") : t("dashboard.voiceRecordBtn")}
            </Button>
          ) : (
            <Button type="button" variant="destructive" className="rounded-full animate-pulse" data-testid={VOICE_RECORDER.stopBtn} onClick={stopRecording}>
              <Square className="mr-2 h-4 w-4" />
              {t("dashboard.voiceStopBtn")}
            </Button>
          )}
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/40">
            <Upload className="h-4 w-4" />
            {t("dashboard.voiceUploadBtn")}
            <input type="file" accept="audio/*" className="hidden" data-testid={VOICE_RECORDER.uploadInput} onChange={handleFileChange} />
          </label>
          {sample && (
            <Button type="button" variant="ghost" className="rounded-full text-destructive hover:text-destructive" data-testid={VOICE_RECORDER.deleteBtn} onClick={handleDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              {t("common.delete")}
            </Button>
          )}
        </div>
      )}

      {pendingBlob && (
        <div className="space-y-3 rounded-xl border border-primary/30 bg-primary/5 p-4">
          <p className="text-sm font-medium">{t("dashboard.voicePreviewLabel")}</p>
          <audio controls src={previewUrl} className="w-full" data-testid={VOICE_RECORDER.pendingPlayer} />
          <div className="flex flex-wrap gap-3">
            <Button type="button" className="rounded-full" data-testid={VOICE_RECORDER.confirmSaveBtn} onClick={uploadPending} disabled={uploading}>
              {uploading ? t("common.loading") : t("common.save")}
            </Button>
            <Button type="button" variant="outline" className="rounded-full" data-testid={VOICE_RECORDER.discardBtn} onClick={() => setPendingBlob(null)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground">{t("dashboard.voiceRecommendations")}</p>
    </div>
  );
}
